import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Circle, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Flame } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SHADES = ["#FFD9CF", "#FFB09E", "#FF8166", "#E5553A", "#B8321C"];
const shade = (n, max) => SHADES[Math.min(SHADES.length - 1, Math.floor((n / Math.max(max, 1)) * SHADES.length))];

function Legend() {
  return (
    <div className="flex items-center gap-2 text-xs muted" data-testid="demand-legend">
      <span>Fewer jobs</span>{SHADES.map((c) => <span key={c} className="w-6 h-3 rounded-sm" style={{ background: c }} />)}<span>More jobs</span>
    </div>
  );
}

export default function DemandHeatmap() {
  const { cats } = useAuth();
  const [category, setCategory] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/demand-map", { params: category ? { category } : {} }).then((r) => setD(r.data)).catch(() => setD({ cells: [], total: 0, days: 90 })); }, [category]);
  const max = d?.cells[0]?.count || 1;
  const label = cats.find((c) => c.slug === category)?.name;
  return (
    <section className="container-x py-14" data-testid="demand-heatmap">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div><span className="eyebrow">Where work is waiting</span><h2 className="h2 mt-2 flex items-center gap-2"><Flame className="text-[#FF8166]" />Job demand near you</h2>
          <p className="muted mt-2 text-sm" data-testid="demand-total">{d ? `${d.total} ${label ? label.toLowerCase() + " " : ""}job${d.total === 1 ? "" : "s"} posted by homeowners in the last ${d.days} days. Tap a shaded area to see how many.` : "Loading demand\u2026"}</p></div>
        <select className="input max-w-[260px]" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Service" data-testid="demand-service-select">
          <option value="">All services</option>{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </div>
      <div className="h-[460px] rounded-2xl overflow-hidden border border-slate-200 relative" data-testid="demand-map">
        <MapContainer center={[53.2, -1.8]} zoom={6} scrollWheelZoom={false} className="h-full w-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {(d?.cells || []).map((c) => (
            <Circle key={`${c.lat},${c.lng}`} center={[c.lat, c.lng]} radius={6000 + 9000 * Math.sqrt(c.count / max)} pathOptions={{ color: shade(c.count, max), fillColor: shade(c.count, max), fillOpacity: 0.55, weight: 1 }}>
              <Popup><div className="text-sm" data-testid="demand-popup"><b>{c.count} job{c.count === 1 ? "" : "s"}</b> in the last {d.days} days<br />{c.town || "This area"}{label && ` \u00b7 ${label}`}</div></Popup>
            </Circle>))}
        </MapContainer>
        {d && d.total === 0 && <div className="absolute inset-0 grid place-items-center bg-white/60 text-sm font-semibold z-[500]" data-testid="demand-empty">No {label ? label.toLowerCase() : ""} jobs posted in the last {d.days} days yet</div>}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4"><Legend /><Link to="/register?role=provider" className="btn-coral btn-sm" data-testid="demand-join-btn">Join and get these jobs</Link></div>
    </section>
  );
}
